<script lang="ts" setup>
import { ref, Ref, computed, WritableComputedRef, PropType, onMounted } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    VueFlowConnectionModel,
    NumberModel,
} from '@/types';
import store from '@/store';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';
import { IconRobot } from '@tabler/icons-vue';
import { Handle, Position } from '@vue-flow/core';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { NumberService } from '@/services';

const { t } = useI18n();

const props = defineProps({
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
});

const showForm = ref(false);
const numberOptions: Ref<NumberModel[]> = ref([]);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

interface IVirtualAgentFormState {
    number: null | NumberModel;
}

const formState: Ref<IVirtualAgentFormState> = ref({
    number: null,
});

const textValidation = t('connectFlow.node.transferVirtualAgent.validation');
const numberRequired = helpers.withMessage(textValidation, (value, state) => {
    return !!state.number;
});

const rules = computed<ValidationArgs<IVirtualAgentFormState>>(() => {
    const r: ValidationArgs<IVirtualAgentFormState> = {
        number: {
            numberRequired,
        },
    };
    return r;
});

const v$: Ref<Validation<ValidationArgs<IVirtualAgentFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferVirtualAgent.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    }
    return text;
});

const sourceHandles = computed(() => {
    const connections: VueFlowConnectionModel[] =
        store.getters['flow/' + ConnectFlowModelingGetters.ELEMENTS_CONNECTIONS];
    const handles = new Set<string>();
    connections
        .filter((c: VueFlowConnectionModel) => c.source === nodeWritable.value.id)
        .forEach((c) => {
            if (c.sourceHandle) {
                handles.add(c.sourceHandle);
            }
        });
    if (handles.size === 0) {
        handles.add('hangupWithoutTransfer');
    }
    return Array.from(handles);
});

const sourceOptionHandles = computed(() =>
    sourceHandles.value.filter((h) => h !== 'hangupWithoutTransfer'),
);

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}

async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value && formState.value.number) {
        nodeWritable.value.data.related = {
            value: formState.value.number.id.toString(),
            label: formState.value.number.number,
        };
        nodeWritable.value.data.virtualAgentNumber = formState.value.number.number;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(async () => {
    try {
        const response = await NumberService.getAll();
        numberOptions.value = response.data;
        if (nodeWritable.value.data.related?.value) {
            formState.value.number =
                numberOptions.value.find(
                    (n) => n.id.toString() === nodeWritable.value.data.related?.value,
                ) || null;
        }
        isFormCorrect.value = !!formState.value.number;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    } catch (error) {
        console.error('Error loading numbers:', error);
    }
});
</script>

<template>
    <UiNode
        :nodeId="node.id"
        :title="$t('connectFlow.node.transferVirtualAgent.title')"
        :icon="IconRobot"
        showActions
        @editNode="showForm = true"
        @removeNode="removeNode"
        :class="isFormCorrect ? '' : 'border-red-300 shadow-red-300'"
    >
        <div class="flex items-center gap-2">
            <span class="font-medium text-gray-600">
                {{ nodeText }}
            </span>
            <div class="group relative">
                <QuestionMarkCircleIcon
                    class="h-5 w-5 cursor-help text-gray-400 hover:text-gray-600"
                />
                <div
                    class="invisible absolute left-0 top-full z-10 mt-1 w-48 rounded bg-gray-800 px-2 py-1 text-xs text-white group-hover:visible"
                >
                    {{ $t('connectFlow.node.transferVirtualAgent.tooltip') }}
                </div>
            </div>
        </div>

        <Handle
            v-for="(handleId, idx) in sourceOptionHandles"
            :key="handleId"
            :id="handleId"
            type="source"
            :connectable="false"
            :position="Position.Bottom"
            :style="`left: ${(idx + 1) * (100 / (sourceOptionHandles.length + 1))}%; bottom: -6px`"
        />

        <Handle
            id="hangupWithoutTransfer"
            type="source"
            :connectable="false"
            :position="Position.Left"
            style="top: auto; bottom: 30px"
        />
    </UiNode>
    <UiRightPanel
        v-model="showForm"
        :title="$t('connectFlow.node.transferVirtualAgent.title')"
        :icon="IconRobot"
    >
        <form autocomplete="off" class="flex flex-col gap-3" @submit.prevent="onSubmit">
            <div class="sm:col-span-12">
                <label class="mb-2 block text-sm font-medium text-gray-800">
                    {{ $t('connectFlow.node.transferVirtualAgent.selectNumber') }}
                </label>
                <UiMultiselect
                    name="number"
                    v-model="v$.number.$model"
                    :options="numberOptions"
                    :errors="v$.number.$silentErrors"
                    label="number"
                    value-prop="id"
                    can-clear
                />
            </div>

            <div class="mt-6 grid grid-cols-2 place-content-around gap-4 text-center">
                <UiButton variant="white" text-variant="gray-700" @click="showForm = false">
                    {{ $t('core.actions.Cancel') }}
                </UiButton>
                <UiButton type="submit" variant="primary" text-variant="white">
                    {{ $t('core.actions.Apply') }}
                </UiButton>
            </div>
        </form>
    </UiRightPanel>
</template>
